import { batch, computed, signal } from '@preact/signals-react';
import { t } from 'i18next';
import { SEVERITY_COLORS, Severity } from '../../../common/enums/Severity';
import { and } from '../../../common/hooks/use-form-item/validators/compositions';
import { required } from '../../../common/hooks/use-form-item/validators/existential';
import { greaterOrEqualThan, lessThan } from '../../../common/hooks/use-form-item/validators/operational';
import { number } from '../../../common/hooks/use-form-item/validators/type';
import { ThresholdUnit } from '../models/Threshold';
import { MAX_INPUT, MIN_INPUT } from '../utils/constants';

export enum CardNodeIndex {
  FIRST = 'FIRST',
  SECOND = 'SECOND',
  THIRD = 'THIRD',
}

export class CardNode {
  // Fields that change value throughout the lifecycle
  // of JSX components are marked with "signal"
  private readonly value = signal(<number>0);
  private readonly enabled = signal(<boolean>false);

  // Fields that do not change value throughout the lifecycle
  // of JSX components are regular class members
  private readonly id: CardNodeIndex;
  private readonly unit: ThresholdUnit;
  private readonly defaultValue: number;
  private readonly bitOrder: boolean;
  private previous: CardNode | null = null;
  private next: CardNode | null = null;

  constructor(id: CardNodeIndex, value: number, enabled: boolean, unit: ThresholdUnit, defaultValue: number) {
    this.id = id;
    this.unit = unit;
    this.defaultValue = defaultValue;
    this.bitOrder = this.unit === 'bits';

    batch(() => {
      this.setValue(value);
      this.setEnabled(enabled);
    });
  }

  getId() {
    return this.id;
  }

  setPrevious(node: CardNode) {
    this.previous = node;
  }

  setNext(node: CardNode) {
    this.next = node;
  }

  getValue() {
    return this.value.value;
  }

  /**
   * Display value is the value shown to the user in UI.
   *
   * Bit values such as [10,20,30] are processed as exclusive
   * ranges in the API. Therefore the low, med, and high
   * buckets will end at [9,19,29] respectively.
   *
   * Day values such as [10,20,30] are processed as inclusive
   * ranges in the API. Meaning that the low, med, and high
   * will truly include [10,20,30].
   *
   * When displaying values to the user we only want to display
   * them in an inclusive range format. Therefore "day" values
   * are displayed as they are while "bit" values are display
   * with a -1.
   *
   */
  getDisplayValue() {
    return this.bitOrder ? this.getValue() - 1 : this.getValue();
  }

  isEnabled() {
    return this.enabled.value;
  }

  getDisableSwitch() {
    return this.disableSwitch.value;
  }

  getFromValue() {
    return this.fromValue.value;
  }

  getToValue() {
    return this.toValue.value;
  }

  isCardValid() {
    if (this.isEnabled()) {
      return this.getValue() > this.getPreviousValue();
    }
    return this.getValue() === this.getPreviousValue();
  }

  disableCard() {
    this.setValue(this.getPreviousValue());
    let previousValue = this.getValue();
    const neighbors = this.succeedingNeighbors();
    for (const neighbor of neighbors) {
      if (!neighbor.isEnabled()) {
        neighbor.setValue(previousValue);
      }
      previousValue = neighbor.getValue();
    }
    this.setEnabled(false);
  }

  enableCard() {
    const closestValue = this.closestEnabledValue();
    this.changeValue(closestValue);
    this.setEnabled(true);
  }

  toggleEnable(check: boolean) {
    if (check) {
      this.enableCard();
    } else {
      this.disableCard();
    }
  }

  onChangeValue(value: string | number) {
    // Bit values we send to api & display is always going to be + 1
    // because the api accepts exclusive ranges while
    // we show the user inclusive ranges.
    // Day values are going to be as is because the API processes
    // them as inclusive ranges.
    const numValue = this.bitOrder ? Number(value) + 1 : Number(value);

    // Prevent user from
    // inserting a number larger than 2^31 - 1
    // relative to it's index
    if (this.validateThresholdNumber(this.upperBound())(numValue) !== true) {
      return;
    }
    this.changeValue(numValue);
  }

  getUnit() {
    return this.unit;
  }

  getDefaultSettingString() {
    let previousDefault = 0;
    if (this.previous) {
      previousDefault = this.previous.defaultValue;
    }
    const displayDefaultValue = this.bitOrder ? this.defaultValue - 1 : this.defaultValue;
    return this.defaultValue === previousDefault ? t('off') : t(`createProfile.units.${this.unit}.count`, { count: displayDefaultValue });
  }

  getBorderColor() {
    switch (this.id) {
      case CardNodeIndex.FIRST:
        return (this.bitOrder ? SEVERITY_COLORS[Severity.HIGH] : SEVERITY_COLORS[Severity.LOW]).main;
      case CardNodeIndex.SECOND:
        return SEVERITY_COLORS[Severity.MED].main;
      case CardNodeIndex.THIRD:
        return (this.bitOrder ? SEVERITY_COLORS[Severity.LOW] : SEVERITY_COLORS[Severity.HIGH]).main;
    }
  }

  getColor() {
    switch (this.id) {
      case CardNodeIndex.FIRST:
        return (this.bitOrder ? SEVERITY_COLORS[Severity.HIGH] : SEVERITY_COLORS[Severity.LOW]).background;
      case CardNodeIndex.SECOND:
        return SEVERITY_COLORS[Severity.MED].background;
      case CardNodeIndex.THIRD:
        return (this.bitOrder ? SEVERITY_COLORS[Severity.LOW] : SEVERITY_COLORS[Severity.HIGH]).background;
    }
  }

  getTitle() {
    switch (this.id) {
      case CardNodeIndex.FIRST:
        return this.bitOrder ? t('high') : t('low');
      case CardNodeIndex.SECOND:
        return t('medium');
      case CardNodeIndex.THIRD:
        return this.bitOrder ? t('low') : t('high');
    }
  }

  private readonly fromValue = computed(() => {
    let previousCard = this.previous;
    while (previousCard !== null) {
      if (previousCard.isEnabled()) {
        return previousCard.getValue();
      }
      previousCard = previousCard.previous;
    }
    return 0;
  });

  private readonly toValue = computed(() => {
    let nextCard = this.next;
    while (nextCard !== null) {
      if (nextCard.isEnabled()) {
        return nextCard.getValue() - 1;
      }
      nextCard = nextCard.next;
    }
    return Infinity;
  });

  private setValue(value: number) {
    this.value.value = value;
  }

  private setEnabled(value: boolean) {
    this.enabled.value = value;
  }

  //return true if all neighbors are switched off otherwise return false
  private readonly disableSwitch = computed(() => {
    const neighbors = this.getAllNeighbors();
    return neighbors.every((neighbor) => !neighbor.isEnabled());
  });

  private precedingNeighbors() {
    const neighbors = [];
    let card = this.previous;
    while (card) {
      neighbors.push(card);
      card = card.previous;
    }
    return neighbors;
  }

  private succeedingNeighbors() {
    const neighbors = [];
    let card = this.next;
    while (card) {
      neighbors.push(card);
      card = card.next;
    }
    return neighbors;
  }

  private getAllNeighbors() {
    return this.precedingNeighbors().concat(this.succeedingNeighbors());
  }

  // closestEnabledValue makes a best effort guess on what
  // value a newly enabled card can take on. To maintain a quality
  // user experience closestEnabledValue picks a value
  // that is closest to the existing values in place. The
  // Math.max(..) function provides a safety mechanism for us
  // to prevent us from choosing a negative value. Therefore the
  // minimum value picked must be 2 or above.
  private closestEnabledValue() {
    for (const neighbor of this.precedingNeighbors()) {
      if (neighbor.isEnabled()) {
        return Math.max(neighbor.getValue() + 1, 2);
      }
    }

    for (const neighbor of this.succeedingNeighbors()) {
      if (neighbor.isEnabled()) {
        return Math.max(neighbor.getValue() - 3, 2);
      }
    }

    return 2;
  }

  private validateThresholdNumber(max: number) {
    return and(required(), number(), greaterOrEqualThan(MIN_INPUT), lessThan(max));
  }

  // Decrement from max upper bound depending on index
  // this is to allow user to reach max
  // number using adjacent cards
  private upperBound() {
    switch (this.id) {
      case CardNodeIndex.FIRST:
        return MAX_INPUT - 4;
      case CardNodeIndex.SECOND:
        return MAX_INPUT - 2;
      case CardNodeIndex.THIRD:
        return MAX_INPUT;
    }
  }

  private changeValue(value: number) {
    this.setValue(value);
    let previousValue = this.getValue();
    const neighbors = this.succeedingNeighbors();
    for (const neighbor of neighbors) {
      if (!neighbor.isEnabled()) {
        neighbor.setValue(previousValue);
      } else {
        const isSmallValue = neighbor.getValue() <= previousValue;
        if (isSmallValue) {
          neighbor.setValue(previousValue + 2);
        }
      }
      previousValue = neighbor.getValue();
    }
  }

  private getPreviousValue() {
    if (this.previous) {
      return this.previous.getValue();
    }
    return 0;
  }
}
